import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import type { SummitSelect } from "~/../drizzle/schema";
import { authMiddleware } from "~/lib/auth-middleware";
import { trackingParametersSchema } from "~/lib/shared/tracking-parameters";

import { getDBWithInit } from "../db";

const optionalTrackingParameters = trackingParametersSchema.optional();

/**
 * Accepts absolute URLs (https://...) or relative paths (/path).
 * Empty strings are also allowed to indicate "no redirect".
 */
const redirectUrlSchema = z
  .string()
  .refine(
    (val) => {
      if (val === "") return true;
      // Allow relative paths
      if (val.startsWith("/")) return true;
      // Validate absolute URLs
      try {
        const parsed = new URL(val);
        return parsed.protocol === "https:" || parsed.protocol === "http:";
      } catch {
        return false;
      }
    },
    { message: "Invalid redirect URL" },
  )
  .optional()
  .or(z.literal(""));

const SummitRegistrationSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(100, "First name too long"),
  email: z.string().email("Valid email is required").max(320, "Email too long"),
  summitId: z.string().uuid("Valid summit ID is required"),
  redirectUrl: redirectUrlSchema,
  trackingParameters: optionalTrackingParameters,
});

const AuthenticatedRegistrationSchema = z.object({
  summitId: z.string().uuid("Valid summit ID is required"),
  redirectUrl: redirectUrlSchema,
  trackingParameters: optionalTrackingParameters,
});

export interface RegistrationResponse {
  summit: SummitSelect;
  redirectUrl: string | undefined;
}

function validateRedirectUrl(url: string | undefined): string | undefined {
  if (!url || url === "") return undefined;

  // Allow relative paths (they're safe and common)
  if (url.startsWith("/")) {
    return url;
  }

  try {
    const parsedUrl = new URL(url);
    // Only allow https URLs in production for security
    if (
      parsedUrl.protocol !== "https:" &&
      process.env.NODE_ENV === "production"
    ) {
      // Fallback to undefined instead of throwing - let the caller use a default
      console.warn(`Redirect URL rejected (non-HTTPS in production): ${url}`);
      return undefined;
    }
    return url;
  } catch {
    // Invalid URL format - fallback gracefully instead of erroring
    console.warn(`Invalid redirect URL format, ignoring: ${url}`);
    return undefined;
  }
}

export const getNextAvailableSummit = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const { getNextFutureSummit, getActiveSummit } =
        await import("~/lib/server/summit");
      // Try future/active summit first, fall back to most recent summit for email capture
      const futureSummit = await getNextFutureSummit();
      const summit = futureSummit ?? (await getActiveSummit());
      if (!summit) {
        return { error: "No summit found", summit: null };
      }
      // When falling back to a past summit, hide the banner after 1 week
      const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
      if (
        !futureSummit &&
        summit.endDate.getTime() + ONE_WEEK_MS < Date.now()
      ) {
        return { error: "No summit found", summit: null };
      }
      return { error: null, summit };
    } catch (error) {
      console.error("Error fetching next available summit:", error);
      return {
        error:
          error instanceof Error ? error.message : "Failed to fetch summit",
        summit: null,
      };
    }
  },
);

export const getNextAvailableSummitOptions = queryOptions({
  queryKey: ["summit", "next-available"],
  queryFn: () => getNextAvailableSummit(),
  staleTime: 5 * 60 * 1000,
});

/**
 * Get the most recently ended summit if it ended within the replay window (3 days).
 * Returns the summit data along with the total number of days for linking to the last day replay.
 */
export const getRecentlyEndedSummit = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const { getRecentlyEndedSummitWithDayCount } =
        await import("~/lib/server/summit");
      const result = await getRecentlyEndedSummitWithDayCount();
      if (!result) {
        return { summit: null, dayCount: 0 };
      }
      return { summit: result.summit, dayCount: result.dayCount };
    } catch (error) {
      console.error("Error fetching recently ended summit:", error);
      return { summit: null, dayCount: 0 };
    }
  },
);

export const getRecentlyEndedSummitOptions = queryOptions({
  queryKey: ["summit", "recently-ended"],
  queryFn: () => getRecentlyEndedSummit(),
  staleTime: 5 * 60 * 1000,
});

export const checkUserSummitRegistration = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    try {
      const { getNextFutureSummit, getUserSummitRegistration } =
        await import("~/lib/server/summit");
      const summit = await getNextFutureSummit();
      if (!summit) {
        return { isRegistered: false, summit: null, error: null };
      }

      const registration = await getUserSummitRegistration(
        context.user.id,
        summit.id,
      );

      return {
        isRegistered: registration.isRegistered,
        summit,
        error: null,
      };
    } catch (error) {
      console.error("Error checking user summit registration:", error);
      return {
        isRegistered: false,
        summit: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to check registration",
      };
    }
  });

export const checkUserSummitRegistrationOptions = queryOptions({
  queryKey: ["summit", "user-registration"],
  queryFn: () => checkUserSummitRegistration(),
  staleTime: 1 * 60 * 1000,
});

export const registerAuthenticatedUserForSummit = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .validator(AuthenticatedRegistrationSchema)
  .handler(async ({ data, context }): Promise<RegistrationResponse> => {
    try {
      const { registerUserForSummitAndSendConfirmation } =
        await import("~/lib/server/summit-registration-workflow");
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await (
        await getDBWithInit()
      ).query.summits.findFirst({
        where: {
          id: data.summitId,
        },
      });
      if (!summit) {
        throw new Error("No upcoming summit found");
      }

      await registerUserForSummitAndSendConfirmation({
        user: context.user,
        summit,
        trackingParameters: data.trackingParameters ?? null,
      });

      return {
        summit,
        redirectUrl: validatedRedirectUrl,
      };
    } catch (error) {
      console.error("Error in authenticated summit registration:", error);
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to register for summit",
      );
    }
  });

export const registerUnauthenticatedUserForSummit = createServerFn({
  method: "POST",
})
  .validator(SummitRegistrationSchema)
  .handler(async ({ data }): Promise<RegistrationResponse> => {
    try {
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await (
        await getDBWithInit()
      ).query.summits.findFirst({
        where: {
          id: data.summitId,
        },
      });

      if (!summit) {
        throw new Error("No summit found");
      }

      const { ensureUserWithClerk } = await import("~/lib/server/user");
      const { user, clerkLinked } = await ensureUserWithClerk({
        email: data.email,
        firstName: data.firstName.trim(),
      });

      if (!clerkLinked) {
        throw new Error("Failed to create or find user in Clerk");
      }

      const { registerUserForSummitAndSendConfirmation } =
        await import("~/lib/server/summit-registration-workflow");

      await registerUserForSummitAndSendConfirmation({
        user,
        summit,
        trackingParameters: data.trackingParameters ?? null,
      });

      const redirectUrl =
        validatedRedirectUrl ||
        `${process.env.NODE_ENV === "production" ? "https://dreamcoachmatch.com" : "http://localhost:3000"}/dashboard`;

      return {
        summit,
        redirectUrl,
      };
    } catch (error) {
      console.error("Error preparing magic link registration:", error);
      throw new Error(
        `Failed to prepare registration: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  });
